/**
 * Frontend Error Handling Utilities
 * 
 * This module provides standardized error handling for API responses,
 * ensuring compatibility with backend error format.
 * 
 * ## Backend Error Format
 * 
 * The backend returns errors in this structure:
 * ```json
 * {
 *   "error": {
 *     "code": "POLICY_NOT_FOUND",
 *     "message": "Policy with ID 123 not found",
 *     "details": { ... },
 *     "correlationId": "abc-123",
 *     "timestamp": "2026-01-08T10:00:00Z"
 *   }
 * }
 * ```
 * 
 * ## Frontend Compatibility
 * 
 * The `ApiError` class and utilities extract this structure from:
 * - OpenAPI generated client errors (wrapped in `response.data`)
 * - Fetch API responses
 * - Axios-style responses
 * - Plain Error objects
 */

/**
 * Standardized API error with backend error code support
 */
export class ApiError extends Error {
  /** HTTP status code (e.g., 404, 500) */
  public readonly status?: number;
  
  /** Backend error code (e.g., "POLICY_NOT_FOUND", "INSUFFICIENT_PERMISSIONS") */
  public readonly code?: string;
  
  /** Additional error details from backend */
  public readonly details?: Record<string, unknown>;
  
  /** Correlation ID for request tracing */
  public readonly correlationId?: string;

  constructor(
    message: string,
    status?: number,
    code?: string,
    details?: Record<string, unknown>,
    correlationId?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.correlationId = correlationId;

    // Maintains proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Type guard for ApiError instances
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Backend error response structure
 */
interface BackendErrorResponse {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
    correlationId?: string;
  };
  // Legacy formats
  code?: string;
  message?: string;
}

/**
 * Error with HTTP response property (OpenAPI client, Axios, etc.)
 */
interface ErrorWithResponse {
  response?: {
    status?: number;
    data?: BackendErrorResponse;
  };
  status?: number;
  data?: BackendErrorResponse;
}

/**
 * Type guard for errors with response property
 */
function isErrorWithResponse(error: unknown): error is ErrorWithResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('response' in error || 'status' in error)
  );
}

/**
 * Check if an error is a 404 Not Found error
 * 
 * Useful for handling missing resources gracefully without showing error messages
 * 
 * @param error - The error to check
 * @returns true if the error is a 404 error
 * 
 * @example
 * ```typescript
 * try {
 *   const subscription = await apiClient.getSubscription({ id });
 *   return subscription;
 * } catch (error) {
 *   if (isNotFoundError(error)) {
 *     return null; // Resource doesn't exist, return null instead of throwing
 *   }
 *   throw error; // Re-throw other errors
 * }
 * ```
 */
export function isNotFoundError(error: unknown): boolean {
  // Check ApiError status
  if (isApiError(error)) {
    return error.status === 404 || error.code === 'NOT_FOUND';
  }
  
  // Check raw error with status property
  if (error && typeof error === 'object' && 'status' in error) {
    return error.status === 404;
  }
  
  // Check error with response property
  if (isErrorWithResponse(error)) {
    const status = error.response?.status ?? error.status;
    const code = error.response?.data?.error?.code ?? error.response?.data?.code;
    return status === 404 || code === 'NOT_FOUND';
  }
  
  return false;
}

/**
 * Utility to create ApiError from various error types, preserving error codes from backend.
 * 
 * This ensures that backend error codes are captured and can be used for specific error handling.
 * 
 * @param error - The error object to convert
 * @returns ApiError instance with message, status, code, details, and correlationId (if available)
 * 
 * @example
 * ```typescript
 * try {
 *   await apiClient.someEndpoint();
 * } catch (error) {
 *   const apiError = createApiError(error);
 *   
 *   // Handle specific error codes from backend
 *   if (apiError.code === 'INSUFFICIENT_PERMISSIONS') {
 *     toast({ title: 'Access Denied', description: 'You do not have permission' });
 *   } else if (apiError.code === 'POLICY_NOT_FOUND') {
 *     router.push('/policies');
 *   } else {
 *     toast({ title: 'Error', description: apiError.message, variant: 'destructive' });
 *   }
 *   
 *   // Log with correlation ID for debugging
 *   console.error(`Request failed [${apiError.code}] (${apiError.correlationId}):`, apiError.message);
 * }
 * ```
 */
export function createApiError(error: unknown): ApiError {
  // Already an ApiError
  if (isApiError(error)) {
    return error;
  }
  
  // Error with HTTP response (OpenAPI client, Axios, fetch wrapper)
  if (isErrorWithResponse(error)) {
    const response = error.response;
    const status = response?.status ?? error.status;
    const data = response?.data ?? error.data;
    
    // Backend error format (nested under "error" key)
    if (data?.error) {
      return new ApiError(
        data.error.message || 'An unexpected error occurred',
        status,
        data.error.code,
        data.error.details,
        data.error.correlationId
      );
    }
    
    // Legacy flat format
    if (data?.code || data?.message) {
      return new ApiError(
        data.message || 'An unexpected error occurred',
        status,
        data.code
      );
    }
    
    // Has response but no data - use status and error message
    if (status) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      return new ApiError(message, status);
    }
  }
  
  // Plain Error object
  if (error instanceof Error) {
    return new ApiError(error.message);
  }
  
  // String error
  if (typeof error === 'string') {
    return new ApiError(error);
  }
  
  // Unknown error type
  return new ApiError('An unexpected error occurred');
}

/**
 * Safely extract error message from any error type
 * 
 * Handles multiple error formats:
 * - Backend error format: `error.response.data.error.message`
 * - OpenAPI client format: `error.body.details.originalMessage`
 * - Standard Error objects
 * - Plain strings
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle object-like errors
  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    
    // OpenAPI generated client format: error.body.details.originalMessage
    const body = err.body as Record<string, unknown> | undefined;
    if (body && typeof body === 'object') {
      const details = body.details as Record<string, unknown> | undefined;
      if (details && typeof details.originalMessage === 'string') {
        return details.originalMessage;
      }
      if (typeof body.message === 'string') {
        return body.message;
      }
    }
    
    // Standard API error with response
    if (isErrorWithResponse(error)) {
      const response = err.response as { data?: unknown } | undefined;
      const data = response?.data ?? err.data;
      if (data && typeof data === 'object') {
        const dataObj = data as Record<string, unknown>;
        // Backend nested format
        if (dataObj.error && typeof dataObj.error === 'object') {
          const errorObj = dataObj.error as Record<string, unknown>;
          if (typeof errorObj.message === 'string') return errorObj.message;
        }
        // Flat format
        if (typeof dataObj.message === 'string') return dataObj.message;
      }
    }
    
    // Direct message property
    if (typeof err.message === 'string') {
      return err.message;
    }
  }
  
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}

/**
 * Extract error code from backend responses
 * 
 * @returns Error code string or undefined if not present
 */
export function getErrorCode(error: unknown): string | undefined {
  if (isApiError(error)) {
    return error.code;
  }
  if (isErrorWithResponse(error)) {
    const data = error.response?.data ?? error.data;
    if (data?.error?.code) return data.error.code;
    if (data?.code) return data.code;
  }
  return undefined;
}

/**
 * Extract correlation ID for request tracing
 * 
 * @returns Correlation ID or undefined
 */
export function getErrorCorrelationId(error: unknown): string | undefined {
  if (isApiError(error)) {
    return error.correlationId;
  }
  if (isErrorWithResponse(error)) {
    const data = error.response?.data ?? error.data;
    return data?.error?.correlationId;
  }
  return undefined;
}

/**
 * Check if an error message contains specific text
 * 
 * Useful for business logic checks like checking for "duplicate" errors
 * 
 * @param error - The error to check
 * @param text - The text to search for (case-insensitive)
 * @returns true if the error message contains the text
 * 
 * @example
 * ```typescript
 * try {
 *   await createUser({ email: 'test@example.com' });
 * } catch (error) {
 *   if (errorContains(error, 'duplicate')) {
 *     toast({ title: 'User already exists' });
 *   }
 * }
 * ```
 */
export function errorContains(error: unknown, text: string): boolean {
  const message = getErrorMessage(error).toLowerCase();
  return message.includes(text.toLowerCase());
}

/**
 * Log error with full context including code and correlation ID
 * 
 * @param error - The error to log
 * @param context - Additional context string (e.g., "Subscription error", "Login failed")
 */
export function logApiError(error: unknown, context?: string): void {
  const code = getErrorCode(error);
  const correlationId = getErrorCorrelationId(error);
  const message = getErrorMessage(error);
  
  const parts: string[] = [];
  if (context) parts.push(context);
  if (code) parts.push(`[${code}]`);
  if (correlationId) parts.push(`(${correlationId})`);
  
  const prefix = parts.length > 0 ? parts.join(' ') + ':' : '';
  console.error(prefix, message, error);
}

/**
 * Async error message extractor that handles OpenAPI generated client ResponseError.
 *
 * The generated client throws `ResponseError` whose `.message` is a generic
 * "Response returned an error code" string. The real backend message lives in the
 * response body: `{ error: { message, code, ... } }`.
 *
 * This helper reads the response body (if present) and falls back through:
 *   1. `body.error.message`  (backend canonical format)
 *   2. `body.message`        (legacy / flat format)
 *   3. Sync `getErrorMessage(error)`
 *
 * @example
 * ```ts
 * onError: async (error: Error) => {
 *   toast({ description: await getApiErrorMessage(error), variant: 'destructive' });
 * }
 * ```
 */
export async function getApiErrorMessage(error: unknown): Promise<string> {
  // Handle ResponseError from OpenAPI generated client
  if (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    typeof globalThis.Response !== 'undefined' &&
    (error as { response: unknown }).response instanceof Response
  ) {
    try {
      const response = (error as { response: Response }).response;
      // Clone so the body can still be consumed elsewhere if needed
      const body = await response.clone().json() as Record<string, unknown>;

      // Backend canonical format: { error: { message: "..." } }
      if (body.error && typeof body.error === 'object') {
        const errObj = body.error as Record<string, unknown>;
        if (typeof errObj.message === 'string') return errObj.message;
      }
      // Flat format: { message: "..." }
      if (typeof body.message === 'string') return body.message;
    } catch {
      // Body wasn't JSON — fall through to sync path
    }
  }

  return getErrorMessage(error);
}
